/**
 * Sanctioned marketing copy — brand-guide.md §5.
 *
 * Any surface that uses these strings must import from here (not copy them).
 * When editorial iterates a message, one edit propagates. The strings are
 * the audit trail for what got shipped.
 *
 * ============================================================================
 * GTM v4 alignment (2026-09-14) — LOCKED positioning
 * ============================================================================
 * gacpro's public home now leads with a segment-agnostic dual-motion story:
 *   BUYING (purple) = external supplier AI GRC (procured AI).
 *   BUILDING (teal) = internal + shipped AI (deployed AI, incl. products
 *                     that ship AI to business/end-user customers).
 * The two accents are a design-system-wide taxonomy — see
 * docs/v4/positioning.md, docs/v4/launch-scope.md, docs/v4/README.md.
 *
 * The GTM-v4 exports are the `gtmV4.*` block at the top of this file. The
 * pre-v4 exports (heroH1s, heroPainPreHeadline, heroGapStrip, threeAudience,
 * etc.) remain in place for legacy consumers (/supplier sub-page, other
 * marketing surfaces) and are marked DEPRECATED where retired from `/`.
 * A rename sweep is a punch-list item after v4 launches.
 */
/* ==========================================================================
 * GTM v4 exports — LOCKED. See docs/v4/positioning.md.
 * ========================================================================== */
export const gtmV4 = {
    hero: {
        /** Small mono uppercase tag above H1 — kept tight per R3 aesthetic Nit. */
        overline: 'SIGNED COMPLIANCE REGISTER',
        /**
         * Load-bearing H1 (v4.1: `signed` renders in `--text`, not a lane color —
         * cross-lane concept must not wear the Buying accent).
         */
        h1Line1: 'AI you buy. AI you build.',
        h1Line2: 'One continuous risk register.',
        /** One-line umbrella subhead. Names the two-sided platform in one breath. */
        subhead: 'Suppliers attest once. Buyers subscribe. The register connects both sides — no re-survey each cycle.',
        /**
         * Editor byline (plain underlined, LEFT-aligned) — reflects the three-layer
         * signature model. The entire phrase is one link to /verify.
         */
        editorBylinePrefix: 'Framework content signed by',
        editorName: 'Steve Nielsen, editor',
        editorBylineSuffix: ' · Per-system attestations signed by their named owners',
        editorBylineHref: '/verify',
        /**
         * Framework chip row (single row, no markers — R3 aesthetic Nit).
         */
        frameworkChips: [
            'NIST AI RMF',
            'ISO 42001',
            'SOC 2 + AI',
            'HIPAA + AI',
            'EU AI Act (fines live · Aug 2026)',
            'SEC IAC (Dec 2025)',
        ],
    },
    /**
     * Paired lane sub-heads. Symmetric type weight, asymmetric hue.
     * BUYING = purple (--buying), BUILDING = teal (--building).
     * See v4-LAUNCH-SCOPE §"Chromatic budget".
     */
    lanes: {
        buying: {
            lane: 'buying',
            microLabel: 'FOR THE AI YOU BUY',
            heading: 'See which suppliers manage their AI systemically — and which sit outside your oversight scope.',
            numericAtom: '22%',
            numericCaption: 'of CISOs have a formal AI vetting process',
            numericSource: 'Panorays 2026',
            ctaLabel: 'Sample supplier register',
            ctaHref: '/buying/samples/supplier-ai-register',
        },
        building: {
            lane: 'building',
            microLabel: 'FOR THE AI YOU BUILD OR SHIP',
            heading: 'See which of your AI systems are governed — so your buyers stop sending 300-row questionnaires.',
            numericAtom: '1 day',
            /** Delta inline with the atom on the same baseline (R4 glance fix). */
            numericDelta: 'vs 6 wks',
            numericCaption: 'Answer any AI questionnaire — every answer already attested',
            numericSource: 'vs 6 weeks industry average',
            ctaLabel: 'Sample builder register',
            ctaHref: '/buying/samples/building-ai-register',
        },
    },
    /**
     * Hero tertiary cluster (LEFT-aligned to page grid).
     * R4 v4.1 swapped the pill/text-link inversion — the broader-audience
     * questionnaire link is now the pill; fractional CISO is a plain text link.
     */
    heroTertiary: {
        questionnairePill: {
            label: 'Responding to an AI questionnaire?',
            href: '/building/questionnaire-response',
        },
        fractionalCisoLink: {
            label: 'Fractional CISO managing multiple clients? See the operator console',
            href: '/consultancy',
        },
    },
    /**
     * "Which room are you in?" section — badge + body + CTA per lane.
     * R4 v4.1 removed the H3 that duplicated the hero H2s. Cards read as
     * badge → paragraph → CTA. Motion 01/02 CTAs are byte-identical to the
     * hero lane CTAs.
     */
    whichRoom: {
        heading: 'Which room are you in?',
        subheading: 'Two priority motions on one signed register.',
        motions: [
            {
                lane: 'buying',
                badge: 'Buying · The AI you procure',
                body: "A supplier-by-supplier AI register, continuously refreshed as suppliers attest. Delta alerts when a supplier's register changes. Board-reportable when your audit committee asks (SEC IAC · Dec 2025). Optional: correlate against your Okta / Entra sign-in logs to surface AI tools employees signed up for outside procurement.",
                ctaLabel: 'Sample supplier register',
                ctaHref: '/buying/samples/supplier-ai-register',
            },
            {
                lane: 'building',
                badge: 'Building · The AI you deploy or ship',
                body: "A per-system register keyed to the frameworks that apply. Whether you're a growth-stage AI company whose product IS the AI (one system, no GRC team) or an enterprise with a hundred deployed features — same register, scoped to what you actually ship. Attest once. Render on demand for audit committees, prospect questionnaires, regulator responses. Aligned to the disclosure trajectory SEC IAC has begun recommending.",
                ctaLabel: 'Sample builder register',
                ctaHref: '/buying/samples/building-ai-register',
            },
        ],
        /** Assessor demoted single line below the two cards. */
        assessorLine: {
            prefix: 'Also for 3PAOs and audit firms:',
            label: 'signed intelligence feed you can cite across engagements',
            href: '/research',
        },
    },
    /**
     * Bottom CTA band (LEFT-aligned per R3 aesthetic).
     * Primary is NEUTRAL solid (not lane-colored). Sample links carry lane
     * micro-labels in their accent hue.
     */
    bottomCta: {
        headline: 'See the register before you commit.',
        supporting: 'Continuous, cited, and defensible on discovery. Every claim traceable to a framework citation or a named owner.',
        primaryLabel: 'Book a demo',
        primaryHref: '/demo',
        sampleLinks: [
            {
                lane: 'buying',
                microLabel: 'FOR SUPPLIERS',
                label: 'Sample supplier register',
                href: '/buying/samples/supplier-ai-register',
            },
            {
                lane: 'building',
                microLabel: 'FOR BUILDERS',
                label: 'Sample builder register',
                href: '/buying/samples/building-ai-register',
            },
        ],
    },
    /* ------------------------------------------------------------------ */
    /*  v3 product-substrate sections (2026-09-15)                         */
    /* ------------------------------------------------------------------ */
    /** Before/after split — spreadsheet vs continuous register. */
    beforeAfter: {
        heading: '300-row spreadsheet vs. continuous register.',
    },
    /** Three-node data flow diagram. */
    dataFlow: {
        heading: 'Attest once. Subscribe continuously. No re-survey.',
    },
    /** Provenance badge section. */
    provenance: {
        heading: 'Every claim signed. Every signer named.',
        subheading: 'Two layers. Named signers. No anonymous scores.',
        verifyLabel: 'Verify signatures',
        verifyHref: '/verify',
    },
    /** Static framework pill row. */
    frameworkPills: {
        heading: 'FRAMEWORKS IN THE REGISTER',
        moreLabel: 'more',
        moreHref: '/research',
    },
    /** Paired lane stats overline. */
    laneStats: {
        overline: 'TWO LANES, ONE REGISTER',
    },
    /** Hero CTA label — used in hero body, header, and mid-page. */
    heroCta: 'Book a demo',
};
/* ==========================================================================
 * GTM v6 exports — AI GRC Bundle positioning. See docs/v6/PRD.md.
 * ========================================================================== */
export const gtmV6 = {
    hero: {
        overline: 'AI GRC BUNDLE',
        h1Line1: 'Turn AI regulations into checks',
        h1Line2: 'your pipeline can enforce.',
        subhead: 'Map obligations, compile policies, and collect signed evidence in one continuous workflow. Every step deterministic, signed, and auditable.',
        editorBylinePrefix: 'Curated by',
        editorName: 'Steve Nielsen, GRC Lead',
        editorBylineSuffix: ' · Every artifact signed by its named owner',
        editorBylineHref: '/verify',
        frameworkChips: [
            'EU AI Act',
            'NIST AI RMF',
            'ISO 42001',
            'Colorado SB 26-189',
            'SOC 2 + AI',
            'FedRAMP 20x',
            'NYC AEDT',
            'California CPPA',
        ],
    },
    primitives: {
        heading: 'Know your obligations. Enforce your policies. Prove what happened.',
        subheading: 'Four primitives, each producing signed artifacts that feed the next. Together they close the loop from legal obligation to pipeline evidence.',
        items: [
            {
                id: 'P1',
                label: 'Classification → Obligation',
                shortName: 'Classify',
                tagline: 'Know what you owe — with citations, not guesses.',
                body: 'Describe your AI system in plain language. The engine evaluates it against every active jurisdiction rule and produces a signed Obligation Set: per-rule citations, effective dates, required controls, and required evidence. When a rule changes, obligations recompute automatically.',
                persona: 'Legal ops · Compliance · Product managers',
                artifact: 'Signed Obligation Set',
            },
            {
                id: 'P2',
                label: 'Disclosure Register',
                shortName: 'Disclose',
                tagline: 'Publish once. Answer every questionnaire from the same source.',
                body: 'Draft disclosures from your repo, BOM, and eval artifacts. Review, sign, and cut a versioned release. Buyers subscribe, pin a version, and get delta alerts when anything material changes. No more re-surveying each cycle.',
                persona: 'Engineering leads · CISOs · Supplier trust',
                artifact: 'Versioned Release + CycloneDX projection',
            },
            {
                id: 'P3',
                label: 'Governance-as-Code',
                shortName: 'Gate',
                tagline: 'Compile obligations into policy your pipeline already runs.',
                body: 'The compiler takes your Obligation Set and your current disclosure, then emits an OPA bundle, OSCAL definitions, and (wave 2) Kyverno and Cedar policies. Your CI pulls the bundle, evaluates it, and posts signed evidence back. A failing gate prints the exact obligation, citation, and fix.',
                persona: 'Platform eng · DevSecOps · AI platform leads',
                artifact: 'OPA Bundle + OSCAL + Evidence Attestations',
            },
            {
                id: 'P0',
                label: 'Blueprint Planner',
                shortName: 'Plan',
                tagline: 'See what your stack can enforce today — and what to add.',
                body: 'Automated inventory from your connectors, repos, and IdP. Deterministic fit scoring against a versioned reference architecture. Gap plan tied to your registered systems, sequenced by the earliest obligation deadline.',
                persona: 'CISOs · Heads of platform · Assessors',
                artifact: 'Living Blueprint + Gap Roadmap',
            },
        ],
    },
    closedLoop: {
        heading: 'The closed loop.',
        subheading: 'A rule change re-computes obligation sets, re-compiles bundles, makes customer gates fail with a citation, produces new evidence, feeds the next release, and alerts subscribers with the delta.',
        nodes: [
            {
                step: 1,
                label: 'Classify',
                title: 'Rules + intake → Obligation Set',
                rows: [
                    { label: 'Jurisdiction rules evaluated', chip: 'Deterministic', chipVariant: 'success' },
                    { label: 'Per-rule citations + effective dates', chip: 'Signed', chipVariant: 'success' },
                    { label: 'Required controls + evidence', chip: 'Cited', chipVariant: 'accent' },
                ],
            },
            {
                step: 2,
                label: 'Compile',
                title: 'Obligations + disclosure → Policy bundle',
                subtitle: 'OPA · OSCAL · Kyverno · Cedar',
                rows: [
                    { label: 'OPA Rego rules with cited deny messages', chip: undefined, chipVariant: 'success' },
                    { label: 'OSCAL component definitions', chip: undefined, chipVariant: 'success' },
                    { label: 'Reproducible: same IR → same bundle', chip: undefined, chipVariant: 'accent' },
                ],
            },
            {
                step: 3,
                label: 'Evidence',
                title: 'Gate results → signed attestations',
                rows: [
                    { label: 'CI gate cites obligation + fix', chip: 'Signed', chipVariant: 'success' },
                    { label: 'Evidence feeds next release', chip: 'Continuous', chipVariant: 'success' },
                    { label: 'Drift detected → owner alerted', chip: 'Live', chipVariant: 'warn' },
                ],
            },
        ],
    },
    determinism: {
        heading: 'Agents propose. Deterministic code decides.',
        subheading: 'Every agent output — rule drafts, disclosure proposals, remediation hints — lands in a review queue. Nothing auto-activates. The rule engine, compiler, and release pipeline are deterministic, golden-tested, and cite provenance on every output.',
        principles: [
            {
                title: 'Cite or abstain',
                body: 'Every proposal carries field-level provenance: quoted spans, source URLs, artifact hashes. A proposal without provenance fails schema validation.',
            },
            {
                title: 'Reproducible',
                body: 'Same intake + same rules snapshot → byte-identical obligation set. Same IR → byte-identical bundle. Across runs, across data planes.',
            },
            {
                title: 'Boundary-declared',
                body: 'LLM providers are selectable per workspace with declared data boundaries. PHI-bearing workloads require an in-boundary provider. Your data stays where you say it stays.',
            },
        ],
    },
    bottomCta: {
        headline: 'See the loop before you commit.',
        supporting: 'From regulation to pipeline gate — deterministic, signed, and auditable. Every claim traceable to a jurisdiction rule or a named owner.',
        primaryLabel: 'Book a demo',
        primaryHref: '/demo',
    },
    heroCta: 'Book a demo',
};
/* ==========================================================================
 * GTM v10 exports — public homepage `/` (refresh 2026-10-26).
 * "Know what applies. Close what's missing." Assess → Map → Measure → Remediate.
 * Source of truth: the ux-adversary run `homepage-insurable-refresh` (3 rounds,
 * prototype/web.html + CHANGELOG.md). Only consumer: packages/web/src/pages/Landing.tsx.
 * ========================================================================== */
export const gtmV10 = {
    brandName: 'GaCPro',
    skipLink: 'Skip to main content',
    nav: {
        primaryLabel: 'Primary',
        jumpLabel: 'Jump to',
        menu: 'Menu',
        close: 'Close',
        links: [
            { href: '#how', label: 'How it works' },
            { href: '#map', label: 'Map' },
            { href: '#measure', label: 'Measure' },
            { href: '#remediate', label: 'Remediate' },
            { href: '#fedramp', label: 'FedRAMP 20x' },
        ],
        /** The mobile jump menu adds the law-watch list and numbers the steps. */
        menuLinks: [
            { href: '#how', label: 'How it works' },
            { href: '#law-watch', label: 'AI law watch' },
            { href: '#map', label: '02 · Map' },
            { href: '#measure', label: '03 · Measure' },
            { href: '#remediate', label: '04 · Remediate' },
            { href: '#fedramp', label: 'FedRAMP 20x' },
        ],
        signIn: 'Sign in',
    },
    /** Owner decision 2026-10-26: one CTA label everywhere. */
    cta: 'Request an assessment',
    /** Visible header label at ≤420px; the accessible name stays `cta` (WCAG 2.5.3). */
    ctaShort: 'Request',
    hero: {
        eyebrow: 'AI compliance, with evidence insurers ask for',
        h1Line1: 'Know what applies.',
        h1Line2: 'Close what’s missing.',
        subhead: 'GaCPro assesses each AI system against the laws, case law, and risk controls that matter, then shows what’s covered, where the gaps are, and how to close them.',
        secondaryCta: 'Explore the platform',
        microStrong: 'Pilot: we map one system with you.',
        micro: 'Built for teams shipping AI in production.',
    },
    /** Illustrative card — sample data, no customer claims. */
    assessmentCard: {
        caption: 'System assessment · Illustrative',
        captionSr: ', sample data',
        pill: 'Assessment mapped',
        systemName: 'Customer Support Copilot',
        systemMeta: 'Production · customer-facing · US / 12 states',
        kpis: [
            { label: 'Obligations', value: '18', sub: 'relevant', tone: 'neutral' },
            { label: 'Covered', value: '15', sub: 'by controls', tone: 'neutral' },
            { label: 'Gaps', value: '3', sub: 'to close', tone: 'gap' },
        ],
        pathLabel: 'A system-specific path to insurer-ready evidence',
        path: [
            { title: 'Applicable rules', sub: 'CO AI Act · CA Chatbots · frameworks', tone: 'rules' },
            { title: 'Controls & coverage', sub: 'Matched · partial · missing', tone: 'rules' },
            { title: 'Remediation', sub: 'Drafted fixes for review', tone: 'remed' },
        ],
        agentName: 'GaCPro agent',
        agentBody: 'maps each gap to a control and drafts the fix; your team reviews and applies it.',
    },
    steps: {
        headingLine1: 'From legal text',
        headingLine2: 'to running controls.',
        lede: 'One traceable chain connects the system you operate to the requirements you need to meet and the evidence you can show.',
        stepSrPrefix: 'Step',
        items: [
            { num: '01', title: 'Assess', body: 'Describe the system, purpose, data, and jurisdictions.' },
            { num: '02', title: 'Map', body: 'Find applicable obligations and relevant controls.' },
            { num: '03', title: 'Measure', body: 'See existing coverage, evidence, and control gaps.' },
            { num: '04', title: 'Remediate', body: 'Get drafted fixes your team reviews and applies.' },
        ],
        nextCueLabel: 'Next section: why now',
    },
    problem: {
        overline: 'Why now',
        headline: 'New AI laws keep arriving. Customers and insurers ask first.',
        body: 'A policy document can’t tell you which rules hit the system you ship, or what’s still open. A passing check today can’t tell you what changed in production tomorrow.',
    },
    map: {
        step: '02 · Map',
        heading: 'Every obligation, traced to a control.',
        body: 'GaCPro matches the laws, case law and frameworks that apply to your system with the controls that meet them. You approve every change before it goes live.',
        frameworksLabel: 'Frameworks',
        frameworks: ['EU AI Act', 'NIST AI RMF', 'ISO 42001', 'SOC 2 + AI', 'FedRAMP 20x'],
        ticks: ['The source behind every match', 'Matched, partial or missing, per control'],
        panel: {
            label: 'Support Copilot · obligations',
            tag: 'Illustrative',
            ariaLabel: 'Illustrative obligation map: three obligations, one matched, one partial, one missing.',
            rows: [
                { k: 'Tell users they’re talking to AI', s: '4 sources', v: 'Chat disclosure banner', chip: 'Matched', tone: 'green' },
                { k: 'Assess impact before release', s: '3 sources', v: 'Release risk review', chip: 'Partial', tone: 'amber' },
                { k: 'Offer a human review path', s: '2 sources', v: 'No control yet', chip: 'Missing', tone: 'red' },
            ],
        },
    },
    measure: {
        step: '03 · Measure',
        heading: 'Know when what’s running stops matching.',
        body: 'GaCPro checks your code and cloud against the controls you approved. When something drifts, you see the control, the resource and the fix in one place.',
        ticks: ['Read-only. We never change your systems.'],
        panel: {
            label: 'Live checks · production',
            tag: 'Illustrative',
            ariaLabel: 'Illustrative monitoring view: one control drifted two hours ago, two holding.',
            rows: [
                { k: 'Prompt logging', s: 'Expected on · observed off', v: '2h ago', chip: 'Drift', tone: 'red' },
                { k: 'Model version pinned', s: 'Expected yes · observed yes', v: '6m ago', chip: 'Holding', tone: 'green' },
                { k: 'PII redaction before model call', s: 'Expected on · observed on', v: '6m ago', chip: 'Holding', tone: 'green' },
            ],
        },
        sourcesLead: 'Read-only',
        /** Creatable connector kinds only (CONNECTOR_MANIFESTS, checked 2026-10-26). */
        sources: ['GitHub', 'AWS', 'GCP', 'Azure', 'Okta', 'OpenAI', 'Anthropic'],
    },
    remediate: {
        step: '04 · Remediate',
        heading: 'Close the gap. Keep the proof.',
        body: 'The agent drafts each fix; your team reviews and applies it. The record keeps the requirement, the control, the check and the result.',
        ticks: [
            'Cited sources for every requirement',
            'Signed, dated results',
            'Answer customer AI questionnaires from the same record',
        ],
        panel: {
            label: 'Gap closed · human review path',
            signed: 'Signed',
            ariaLabel: 'Illustrative evidence record: gap closed, fix drafted by the agent and applied by your team, check passed, signed, exportable including an insurer evidence pack.',
            chain: [
                { k: 'Requirement', v: 'Offer a human review path' },
                { k: 'Fix', v: 'Drafted by agent · applied by your team' },
                { k: 'Check', v: 'Escalation route live in chat' },
                { k: 'Result', v: 'Passed · Oct 24' },
            ],
            exportLabel: 'Export:',
            exports: ['PDF', 'JSON', 'Questionnaire answers', 'Insurer evidence pack'],
        },
    },
    fedramp: {
        overline: 'Selling to government',
        heading: 'The same evidence feeds FedRAMP 20x.',
        body: 'The controls and results you already track become inputs your team reviews for its 20x path. Your team keeps ownership of the submission.',
        flowLabel: 'From controls to FedRAMP 20x reporting inputs',
        flow: ['Controls', 'Checks', 'Evidence', '20x reporting inputs'],
    },
    pilot: {
        heading: 'Request an assessment',
        intro: 'GaCPro is in pilot. Join the wait list and we’ll map one AI system with you: what applies, what’s covered, and what’s missing.',
        trustLine: 'Read-only connectors. We collect configuration and aggregates, not prompts or patient data.',
        talkFirst: 'Prefer to talk first?',
        bookDemo: 'Book a demo',
        newTab: ' (opens in a new tab)',
    },
    pilotForm: {
        required: 'All fields are required unless marked optional.',
        optional: '(optional)',
        name: 'Name',
        nameError: 'Enter your name.',
        email: 'Work email',
        emailError: 'Enter a work email, like name@company.com.',
        company: 'Company',
        companyError: 'Enter your company name.',
        ownerLegend: 'Whose system?',
        // Values are the stored keys (@aigovops/core vocabularies/pilot-signup.ts).
        ownerOptions: [
            { value: 'own', label: 'My company’s' },
            { value: 'client', label: 'A client’s' },
        ],
        driverLabel: 'What’s driving this?',
        driverPlaceholder: 'Choose one',
        // Values are the stored keys (@aigovops/core vocabularies/pilot-signup.ts; the Worker accepts only these).
        driverOptions: [
            { value: 'customer_questionnaire', label: 'Customer questionnaire' },
            { value: 'insurer_request', label: 'Insurer request' },
            { value: 'audit', label: 'Audit' },
            { value: 'other', label: 'Other' },
        ],
        /** Driver values that show the "On a deadline?" booking line on success. */
        deadlineDrivers: ['customer_questionnaire', 'insurer_request'],
        submit: 'Request an assessment',
        submitting: 'Sending…',
        genericError: 'We couldn’t send your request. Try again, or book a time instead.',
        successTitle: 'Request received',
        successBefore: 'We’ll email you at ',
        successAfter: ' to schedule your assessment.',
        edit: 'Wrong email? Edit your details',
        deadline: 'On a deadline? Book a time now.',
    },
    footer: {
        copyright: '© 2026 GaCPro · gacpro.io',
        navLabel: 'Footer',
        links: [
            { href: '/signin', label: 'Sign in' },
            { href: '/privacy', label: 'Privacy' },
            { href: '/terms', label: 'Terms' },
            { href: '/building/questionnaire-response', label: 'Responding to an AI questionnaire?' },
            { href: '/consultancy', label: 'Fractional CISO console' },
        ],
    },
};
/* ==========================================================================
 * AI LAW WATCH — homepage ticker + the static #law-watch list.
 *
 * Source: the v11.7 verified worksheet (input/ai-law-watch-ticker.csv in the
 * ux-adversary run homepage-insurable-refresh), with the 11.7 corrections
 * (input/ticker-corrections-from-11.7.md). Primary sources only. Every entry
 * cites its worksheet row. Order = ticker order: the one fully in-force law
 * first, then by next key date.
 *
 * `keyDate` is when duties bind (one consistent "key date" framing).
 *  - status 'in-force'  : already fully in force; shown as "In force", no date.
 *  - status 'partly'    : in force now, with later duties on `keyDate`
 *                         (`dutyLabel` names them).
 *  - status 'upcoming'  : duties bind on `keyDate`.
 * ========================================================================== */
/**
 * The date every entry below was last verified against its primary source.
 * Re-check it as a step of every weekly release (docs/releases runbook); it is
 * the only place the "Verified" date lives.
 */
export const lawWatchVerifiedOn = '2026-10-26';
export const lawWatch = [
    // worksheet row 263 — effective 2026-06-11 (already in force)
    { row: 263, state: 'WA', stateName: 'Washington', name: 'Prior Auth AI', bill: 'SB 5395', status: 'in-force' },
    // worksheet row 38 — regs in force since 2026-01-01; compliance date 2027-01-01
    { row: 38, state: 'CA', stateName: 'California', name: 'CCPA ADMT Rules', bill: 'CPPA ADMT regulations', status: 'partly', keyDate: '2027-01-01', dutyLabel: 'ADMT duties' },
    // worksheet row 36 — Act operative 2026-08-02; platform duties begin 2027-01-01
    { row: 36, state: 'CA', stateName: 'California', name: 'AI Transparency Act', bill: 'SB 942 / AB 853', status: 'partly', keyDate: '2027-01-01', dutyLabel: 'platform duties' },
    // worksheet row 54 — AG enforcement paused by court order (X.AI LLC v. Weiser, D. Colo. 1:26-cv-01515)
    { row: 54, state: 'CO', stateName: 'Colorado', name: 'Colorado AI Act', bill: 'SB 26-189', status: 'upcoming', keyDate: '2027-01-01', tickerNote: 'enforcement paused', note: 'Attorney General enforcement paused by court order' },
    // worksheet row 42
    { row: 42, state: 'CA', stateName: 'California', name: 'Customer Service Chatbots', bill: 'AB 1609', status: 'upcoming', keyDate: '2027-01-01' },
    // worksheet row 186
    { row: 186, state: 'NY', stateName: 'New York', name: 'RAISE Act', bill: 'GBL art. 44-B', status: 'upcoming', keyDate: '2027-01-01' },
    // worksheet row 96
    { row: 96, state: 'IL', stateName: 'Illinois', name: 'AI Safety Measures Act', bill: 'SB 315', status: 'upcoming', keyDate: '2027-01-01' },
    // worksheet row 69
    { row: 69, state: 'CT', stateName: 'Connecticut', name: 'AI Companions', bill: 'SB 5 (PA 26-15)', status: 'upcoming', keyDate: '2027-01-01' },
    // worksheet row 264
    { row: 264, state: 'WA', stateName: 'Washington', name: 'AI Companions', bill: 'ESHB 2225', status: 'upcoming', keyDate: '2027-01-01' },
    // worksheet row 201
    { row: 201, state: 'OR', stateName: 'Oregon', name: 'AI Companions', bill: 'SB 1546', status: 'upcoming', keyDate: '2027-01-01' },
    // worksheet row 56
    { row: 56, state: 'CO', stateName: 'Colorado', name: 'Conversational AI', bill: 'HB 26-1263', status: 'upcoming', keyDate: '2027-01-01' },
    // worksheet row 244
    { row: 244, state: 'UT', stateName: 'Utah', name: 'Content Provenance', bill: 'HB 276', status: 'upcoming', keyDate: '2027-01-01' },
    // worksheet row 184
    { row: 184, state: 'NY', stateName: 'New York', name: 'Safe by Design Act', bill: 'A 10008-C Part Y', status: 'upcoming', keyDate: '2027-01-01' },
    // worksheet row 122 — general privacy act, labelled by its AI part (11.7 correction)
    { row: 122, state: 'LA', stateName: 'Louisiana', name: 'Automated-decision opt-out', bill: 'SB 386', status: 'upcoming', keyDate: '2027-01-01' },
    // worksheet row 265
    { row: 265, state: 'WA', stateName: 'Washington', name: 'AI Content Provenance', bill: 'E2SHB 1170', status: 'upcoming', keyDate: '2027-02-01' },
    // worksheet row 3
    { row: 3, state: 'AL', stateName: 'Alabama', name: 'Automated Decisions', bill: 'HB 351', status: 'upcoming', keyDate: '2027-05-01' },
    // worksheet row 43 — most duties operative 2027-07-01
    { row: 43, state: 'CA', stateName: 'California', name: 'Adam’s Law', bill: 'SB 1119', status: 'upcoming', keyDate: '2027-07-01' },
    // worksheet row 104 — duties apply 2027-07-01
    { row: 104, state: 'IA', stateName: 'Iowa', name: 'Conversational AI', bill: 'SF 2417', status: 'upcoming', keyDate: '2027-07-01' },
    // worksheet row 88
    { row: 88, state: 'ID', stateName: 'Idaho', name: 'Conversational AI Safety', bill: 'S 1297', status: 'upcoming', keyDate: '2027-07-01' },
    // worksheet row 154
    { row: 154, state: 'NE', stateName: 'Nebraska', name: 'Conversational AI Safety', bill: 'LB 525', status: 'upcoming', keyDate: '2027-07-01' },
    // worksheet row 66 — sections in effect since 2026-10-01; duties apply 2027-10-01
    { row: 66, state: 'CT', stateName: 'Connecticut', name: 'AI Hiring Tools', bill: 'SB 5 (PA 26-15) secs. 7–12', status: 'partly', keyDate: '2027-10-01', dutyLabel: 'duties' },
    // worksheet row 254 — general privacy act, labelled by its AI part (11.7 correction)
    { row: 254, state: 'VT', stateName: 'Vermont', name: 'Automated-decision rights', bill: 'S.71', status: 'upcoming', keyDate: '2028-01-01' },
    // worksheet row 46 — statute effective 2027-01-01; registration required from 2029-01-01
    { row: 46, state: 'CA', stateName: 'California', name: 'AI Auditor Registry', bill: 'AB 1405', status: 'upcoming', keyDate: '2029-01-01' },
];
/** UI strings for the law-watch ticker and list (LawWatchTicker / LawWatchList). */
export const lawWatchCopy = {
    title: 'AI LAW WATCH',
    regionLabel: 'AI law watch: key dates',
    verified: 'Verified',
    showAll: 'Show all',
    showAllLong: 'key dates',
    pause: 'Pause AI law watch',
    stripLabel: 'AI law watch entries, scroll sideways',
    inForce: 'In force',
    inForceNow: 'in force now',
    keyDate: 'Key date',
    alreadyInForce: 'Already in force.',
    listHeading: 'AI law watch',
    listGroupNow: 'In force now',
    law: 'law',
    laws: 'laws',
    bindAlready: 'bind; already in force',
    sourceNote: 'Primary sources only. Dates are when duties bind.',
    listCtaLead: 'Which of these apply to your system?',
};
/* ==========================================================================
 * Pre-v4 exports (retained for legacy consumers) — DO NOT USE ON `/`.
 * ========================================================================== */
/**
 * §5.1 — three sanctioned landing-page H1s.
 *
 * Locked 2026-08-26 via the landing-three-audience ux-adversary R2:
 *  - `a` is the winning blended H1 (Keenan-Sexton pain-state + glance mobile
 *    discipline). Wraps ≤4 lines on 375px.
 *  - `b` is the glance-critic backup ("mobile-density-first").
 *  - `c` is a posture-forward alternative for A/B against the pain-state lead.
 *
 * All three carry the same locked positioning promise
 * (business-model.md §0.0, 2026-08-25): certification-ready + emerging AI
 * risk gaps ahead of legislation. The subhead + eyebrow deliver the
 * two-part frame; the H1 carries the specific pain in the buyer's voice.
 */
export const heroH1s = {
    a: "Answer your prospect's AI questionnaire — cited, defensible, signed.",
    b: "Answer every AI compliance questionnaire — and the ones that haven't been written yet.",
    c: 'Every claim in your AI compliance posture, sourced and signed.',
};
/**
 * Mono eyebrow above the H1. Delivers the certification-frameworks roster
 * so the H1 can stay pain-first and ≤10 words. Letter-spaced uppercase-esque
 * mono treatment in the rendered component.
 */
export const heroEyebrow = 'NIST AI RMF · ISO 42001 · SOC 2 · FedRAMP · HITRUST · + emerging AI risk';
/**
 * Subhead carries the two-part positioning promise:
 * certification-ready + the emerging AI risk gaps legislation hasn't
 * caught up to yet.
 */
export const heroSubhead = "Shows what you can prove, flags what you can't, and never lets you confuse the two. Every response cites your documentation or tells you exactly what's missing.";
/**
 * CTAs restructured 2026-08-26 per the landing-three-audience R2 arbitration:
 * the ungated sample-report is the primary artifact the hero shows off; the
 * paste-a-questionnaire self-serve flow is deferred behind Book-a-demo.
 * Retires the pre-auth /app/questionnaires/new dead-end that R1 shipped.
 */
export const heroPrimaryCtaLabel = 'See a sample questionnaire response';
export const heroReadinessCtaLabel = 'See a sample readiness report';
export const heroSecondaryCtaLabel = 'Book a demo';
export const heroMeta = 'No credit card required.';
/**
 * §D7 — Artifact preview strip.
 *
 * Captions must be honest about mockup vs live artifact. Both thumbnails
 * carry a "sample" label in their caption text so a prospect never mistakes
 * a marketing preview for their own data.
 */
export const artifactPreview = {
    report: {
        thumbnailSrc: '/sample-questionnaire-thumb.svg',
        thumbnailAlt: 'Sample questionnaire response — A/B/C format with evidence-backed and aspirational answers',
        href: '/sample-questionnaire.pdf',
        hrefTarget: '_blank',
        caption: 'Sample questionnaire response — every answer cites your documentation or flags the gap. Open the full PDF.',
    },
    boardView: {
        thumbnailSrc: '/sample-board-view.svg',
        thumbnailAlt: 'Sample board view — per-framework readiness score with gap counts and named owners',
        href: '/sample-board-view.svg',
        hrefTarget: '_blank',
        caption: 'Sample board view — mockup, feature roadmap Q2 2027. Shape and inputs finalized; live data on paid tiers when the feature ships.',
    },
};
/**
 * §5.3 — "how it's different" cards.
 *
 * Locked 2026-08-26 via the landing-three-audience ux-adversary R2: the
 * "Ship the answer, not the theory" pillar (previously only in `pillars`)
 * is restored to the landing as card 4. Carries the certification-templates
 * half of the locked positioning ({SOC 2 + AI, ISO 42001, FedRAMP}).
 */
export const diffCards = [
    {
        title: 'A stream, not a snapshot.',
        body: 'Static registers age the day they ship. Every update passes through a named subject-matter expert; critical regulatory changes prioritized within 48 hours, routine updates on a weekly review cadence.',
    },
    {
        title: 'Every claim, sourced.',
        body: 'No black-box scores. Click any item to see the law, clause, or case it comes from — and when we last reviewed it.',
    },
    {
        title: 'Ship the answer, not the theory.',
        body: 'Every questionnaire response cites your uploaded policies, SOPs, and monitoring evidence — or tells you exactly what’s missing. Aspirational gaps become your team’s improvement roadmap, not a hidden liability.',
    },
    {
        title: 'Your data stays where it belongs.',
        body: 'Cloud for speed. On-prem or air-gapped when your CISO says so. Same product, same freshness, delivered by signed subscription package.',
    },
];
/** §4 — four messaging pillars (marketing one-pager). */
export const pillars = [
    {
        title: 'A stream, not a snapshot.',
        body: 'Every register item carries a last-reviewed timestamp and a source citation. Legislative and framework changes flow through the agentic research pipeline into a named-editor review queue; critical changes prioritized within 48 hours, routine updates on a weekly cadence.',
        proofCodes: ['48h critical SLA', 'Named reviewer'],
    },
    {
        title: 'Every claim, sourced.',
        body: 'Provenance metadata on every register item — source, research agent ID, human reviewer ID, review timestamp. Board-ready exports include the citation chain.',
        proofCodes: ['EU-AI-33.1', 'NIST GV-1.1'],
    },
    {
        title: 'Ship the answer, not the theory.',
        body: 'Questionnaire responses backed by your actual documentation — policies, SOPs, monitoring evidence — not just framework criteria. Where evidence is missing, the gap is flagged so your team can fill it before the buyer asks.',
        proofCodes: ['SOC 2', 'ISO 42001'],
    },
    {
        title: 'Meets you where your data lives.',
        body: 'Cloud when you want it. On-prem or air-gapped when your CISO says you have to. Same product, same register, same research feed — delivered via signed subscription packages to isolated environments.',
        proofCodes: ['On-prem', 'Air-gap'],
    },
];
/**
 * §5.2 — proof-strip framework list.
 *
 * Split into two rows so the landing tells the truth in one glance:
 * `proofFrameworksLive` is the shipped register (real items live in the
 * customer product today); `proofFrameworksInReview` is the editorial
 * backlog with named-editor + ETA attribution in the footnote. The two
 * rows render as two ProofStrip instances on Landing so the labels
 * (`Live` vs `In editorial review`) are unambiguous.
 *
 * Numbers are the ground truth as of the Q3 2026 audit; update as
 * content ships. See docs/v1/track-c-plus-fedramp-data-plan.md.
 */
export const proofFrameworksLive = [
    'NIST AI RMF',
    'ISO 42001',
    'EU AI Act',
    'FedRAMP Rev 5',
    'SOC 2 + AI',
    '6 US states',
];
export const proofFrameworksInReview = [
    'HIPAA + AI',
    'HITRUST v11',
    'FDA GMLP',
    'HHS OCR §1557',
    '42 CFR Part 2',
    'FedRAMP 20x',
    '27-state coverage',
];
/** Back-compat alias — old importers that expect a single flat list. */
export const proofFrameworks = proofFrameworksLive;
export const proofFootnote = 'In editorial review by the GRC lead — ETA Q4 2026 for healthcare frameworks; Q1 2027 for FedRAMP 20x and full state-law coverage. Applicability rules live now on every framework.';
/**
 * §D10 — pre-order affordance for the "in editorial review" row.
 *
 * Renders beneath `proofFootnote` on the in-review ProofStrip. Converts the
 * roadmap into a commitment device for buyers whose primary framework is
 * still in review (Priya's HealthTech case in the adversary panel).
 */
export const inReviewPreorderNote = 'Pre-order the HealthTech or FedRAMP pack — locked-in price, coverage-live SLA in your signed contract.';
export const inReviewPreorderMailto = 'mailto:hello@gacpro.io?subject=Pre-order%20framework%20pack';
/**
 * Trust tags for the CISO-comfort strip.
 *
 * Each note must match the corresponding row on /trust exactly — the trust
 * page is the source-of-truth expansion. Aspirational status uses "Roadmap"
 * or "On request"; never "Attested" unless a report actually exists.
 *
 * §D6 — the SOC 2 note carries a bridge line (pentest + SIG Lite +
 * on-prem workaround) because "Roadmap, no committed date" alone was
 * procurement-lethal in the adversary panel.
 */
export const trustTags = [
    {
        title: 'SOC 2 Type II',
        note: 'Roadmap. Bridge: pentest report + pre-answered SIG Lite on request. On-prem / air-gap tier available today for security-blocked buyers.',
        status: 'roadmap',
    },
    { title: 'Sub-processors', note: 'WorkOS + Cloudflare. Full list on /trust.', status: 'live' },
    { title: 'Data residency', note: 'US today. EU on request.', status: 'live' },
    { title: 'Data-flow diagram', note: 'Published on /trust; NDA version on request.', status: 'live' },
    { title: 'BAA template', note: 'For PHI-adjacent tenants — on request.', status: 'live' },
    { title: 'On-prem / air-gap', note: 'Enterprise engagement — Phase 6.', status: 'live' },
    { title: '48h critical SLA', note: 'On critical regulatory updates; routine updates on a weekly cadence.', status: 'live' },
    { title: 'Editorial masthead', note: 'Named editors, verified credentials. See /editors.', status: 'live' },
];
export const trustHeading = 'Trust & Security';
export const trustSubheading = "Everything your CISO will ask for before greenlighting the eval.";
/**
 * Pricing rows — Drata-style "tier name IS the audience" pattern.
 *
 * Locked 2026-08-26 via the landing-three-audience ux-adversary R2 +
 * competitive-landscape-summary.md §Pricing (updated 2026-08-26). Every
 * direct competitor in this tier (Vanta / Drata / Secureframe / Credo AI /
 * OneTrust) has migrated to opaque pricing on their public pricing pages;
 * dollar figures never appear. Nadia's ≤$15K/yr fractional-CISO
 * self-qualification signal is delivered by the tier NAME + one-line
 * audience descriptor, not by a number.
 *
 * The `amount` field is retained on this contract for backward-compat with
 * existing PricingRowList consumers; a follow-up cluster removes the column
 * from the DS component (see punch list). All rows carry the same
 * matter-of-fact "Talk to us" string until then.
 *
 * Fourth row (Assessor · Consultancy) is the Research Subscription SKU
 * per business-model.md §3.5 + competitive-landscape §Pricing — sold into
 * 3PAO firms + GRC consultancies who resell insight (Jordan Whitfield ICP).
 */
export const pricingRows = [
    {
        name: 'Respond',
        plan_id: 'respond',
        lane: 'building',
        description: 'Answer a disclosure request from an enterprise buyer',
        amount: 'Free',
        subtitle: 'Per-system entry, sign, release',
        popular: false,
    },
    {
        name: 'Growth Building',
        plan_id: 'growth-building-annual',
        lane: 'building',
        description: 'Continuous signed register for the AI you build',
        amount: '$9,000/yr',
        subtitle: 'Unlimited systems · questionnaire answering · evidence binding',
        popular: true,
    },
    {
        name: 'Growth+ Building',
        plan_id: 'growth-plus-building-annual',
        lane: 'building',
        description: 'Connectors, Layer 3 counter-sign, board-deliverable renderer',
        amount: '$32,000/yr',
        subtitle: 'Everything in Growth + enterprise connectors',
        popular: false,
    },
    {
        name: 'Enterprise',
        plan_id: 'enterprise',
        lane: null,
        description: 'Fortune 1000 governing internal AI + assessing supplier AI posture',
        amount: 'Talk to us',
        subtitle: 'Both lanes · org roll-up · SSO/SCIM · custom cadence',
        popular: false,
    },
    {
        name: 'Buying Portfolio',
        plan_id: 'buying-portfolio',
        lane: 'buying',
        description: 'Continuous oversight of the AI your company buys',
        amount: '$35–60K/yr',
        subtitle: 'Supplier scoring · disclosure requests · drift alerts',
        popular: false,
    },
];
export const pricingHeading = 'Pricing';
export const pricingLede = 'Five tiers: Building, Buying, or both. Respond is free. Annual invoicing — no credit card required.';
/**
 * §D5 — editor honesty line.
 *
 * NOTE (2026-08-19): The Named Editors section was removed from the Landing
 * page in favour of a subtler treatment (attribution via FreshnessStamp only,
 * plus the /editors page as the discoverable masthead). This string is kept
 * exported for the /editors page and any future surface that wants a
 * standalone honesty statement. Do not re-add it to Landing unless the
 * strategy explicitly re-opens D5.
 */
export const editorHonestyLine = 'Editor-in-chief: Steve Nielsen. Second editor hiring open — trigger: first 3 growth logos OR first FedRAMP customer. Roster expands, this line updates.';
/**
 * §D2 — the seven auditor questions.
 *
 * The highest-signal artifact from the adversary panel (both Nadia and
 * Priya named it as the forwardable eval rubric). Ships on Landing between
 * DiffCardGrid and TrustTagStrip; re-used on /research at full size.
 *
 * Format constraint: every entry has a gacpro answer + a linked evidence
 * href to something publicly reachable. If the evidence isn't public yet,
 * link to the closest honest thing (/editors, or a request-by-email link)
 * and pledge the specific artifact. /trust is withdrawn until v10.7 rebuilds
 * it (owner, 2026-10-30), so don't link it.
 *
 * Evidence links prefer the public-facing surfaces we ship today:
 * - `/editors` (public masthead)
 * - `/sample-report.pdf` (public sample)
 * When a deeper anchor doesn't exist yet, link to the parent surface.
 */
/**
 * §B6 — downloadable buyer's guide PDF.
 *
 * The PDF is generated from these same `buyersGuide*` constants by
 * `packages/web/scripts/build-buyers-guide-pdf.mjs`; the on-page checklist
 * and the PDF export share a single source. Rebuild the PDF whenever
 * `buyersGuideQuestions` or `buyersGuideHeading` change (or `npm run
 * buyers-guide-pdf` from packages/web).
 */
export const buyersGuidePdfHref = '/buyers-guide.pdf';
export const buyersGuideHeading = 'The seven questions your auditor will ask.';
export const buyersGuideLede = 'Print this. Bring it to every vendor demo — including ours. If they can\'t answer these on the spot, that\'s your answer. Every answer below links to evidence, or to a way to ask us for it; nothing is a screenshot from the sales deck.';
export const buyersGuideQuestions = [
    {
        q: 'Show me the source behind one register item — right now.',
        why: 'If citations are missing, aggregated, or "coming soon," documentation is theatre. Independent research finds 86% of tools fail this test on live demos (Grant Thornton).',
        fedsecAnswer: {
            short: 'Every register item carries the exact framework code (NIST-AI-RMF-GV-1.1, ISO 42001 A.6.1.1, EU AI Act Art. 15) and links to the primary source. The sample readiness report shows the citation trail end-to-end.',
            evidenceHref: '/sample-report.pdf',
            evidenceTarget: '_blank',
            evidenceLabel: 'Open the sample report',
        },
    },
    {
        q: 'When did a real human last review this item, and who?',
        why: '"Continuously updated" without a signed reviewer is a scraper. Ask for the name, credentials, and the review timestamp.',
        fedsecAnswer: {
            short: 'Every published register item shows who reviewed it and when. Every editor we show links to a real public profile under the same name.',
            evidenceHref: '/editors',
            evidenceLabel: 'See the editorial masthead',
        },
    },
    {
        q: 'What is your SLA for a critical regulatory change?',
        why: 'The EU AI Act moves in weeks. A quarterly refresh cadence is not a governance product.',
        fedsecAnswer: {
            short: 'Critical regulatory changes are due for editor review within 48 hours; routine updates within 7 days. Every pending change carries its deadline, and the review queue works in deadline order.',
            evidenceHref: 'mailto:hello@gacpro.io?subject=Security%20details%20request',
            evidenceLabel: 'Request security details',
        },
    },
    {
        q: 'Where does my data live during a questionnaire draft?',
        why: 'If it touches a third-party LLM, ask which one, on what terms, and whether it trains on your inputs.',
        fedsecAnswer: {
            short: 'Your data is stored on Cloudflare. AI features send your text only to the model your workspace allows — by default Cloudflare Workers AI. We use OpenAI only to draft rules from public legislative text, never on your content. EU data residency and on-prem are not offered today.',
            evidenceHref: 'mailto:hello@gacpro.io?subject=Security%20details%20request',
            evidenceLabel: 'Request security details',
        },
    },
    {
        q: 'How do you handle a framework I use that\'s not on your list?',
        why: 'Manual import is fine. "Roadmap" without an ETA is not.',
        fedsecAnswer: {
            short: 'Applicability engine live on every framework today; content shipping quarterly with named ETA per pack. HealthTech and FedRAMP 20x packs are pre-orderable with a coverage-live SLA in the signed contract.',
            evidenceHref: 'mailto:hello@gacpro.io?subject=Framework%20coverage%20question',
            evidenceLabel: 'Ask about a specific framework',
        },
    },
    {
        q: 'Can I export everything — register, evidence, citations — as machine-readable files?',
        why: 'If the answer is "PDF only," you\'re renting your own compliance data.',
        fedsecAnswer: {
            short: 'Each AI system exports as CycloneDX, OSCAL or OCSF files, and assessment packages export signed, so anyone can check them at /verify. PDF is a rendering, not the source.',
            evidenceHref: 'mailto:hello@gacpro.io?subject=Sample%20export%20request',
            evidenceLabel: 'Ask for a sample export',
        },
    },
    {
        q: 'What percentage of your customers renewed last year?',
        why: '44% of AI GRC pilots never scale (Drata). Ask about renewal, not logo count.',
        fedsecAnswer: {
            short: 'First cohort renewals report Q1 2027. This row will be published here with real numbers on that date — no cherry-picking. Until then: our answer is "we don\'t have one yet," which is the honest answer.',
            evidenceHref: 'mailto:hello@gacpro.io?subject=Renewal%20disclosure',
            evidenceLabel: 'Ask for the renewal disclosure',
        },
    },
];
/** §D3 — /research nav entry. */
export const researchLinkLabel = 'Research';
export const researchLinkHref = '/research';
/** §5.5 — onboarding empty state. */
export const onboardingEmpty = {
    title: 'Add your first AI system.',
    description: "We'll filter our register (60 items live across 6 frameworks; growing weekly) to only what applies to it, and give you a readiness score within a minute.",
};
/* ============================================================================ */
/* Landing R2 — locked 2026-08-26                                               */
/*                                                                              */
/* Copy strings for the four new landing sections introduced by the             */
/* landing-three-audience ux-adversary run:                                     */
/*   - heroGapStrip      — current-state → future-state one-liner (Keenan-      */
/*                        Sexton Gap Selling anchor). Below hero CTAs.          */
/*   - threeAudience     — Supplier / Enterprise / Auditor parallel entry       */
/*                        cards. Below hero.                                    */
/*   - taxonomy          — 5-type AI classification editorial spread. Breaks    */
/*                        the card-grid cascade per aesthetic critic ruling.    */
/*   - comparative       — 3 buyer-objection-first cards (Vanta/Drata,          */
/*                        Credo AI, OneTrust/ServiceNow). Voice-of-customer.    */
/*   - researchSubscription — Auditor / consultancy motion pitch + tier         */
/*                        ladder (Standard / Priority / Real-Time).             */
/*   - manifestNote      — cleaned auditor-artifact note. Deep-links to /verify.*/
/*                                                                              */
/* Consumed by DS components introduced in the R2 code-apply cluster.           */
/* ============================================================================ */
/**
 * Keenan-Sexton Gap Selling anchor line. One-line current-state →
 * future-state contrast. Ships as a small mono strip between the hero
 * CTAs and the audience block. Two clauses, no long-form narrative
 * (aesthetic override rule).
 */
export const heroGapStrip = {
    current: '8 hours per questionnaire, ad-hoc answers, no defensible sources.',
    future: 'Cited, evidence-backed draft in 15 minutes — gaps flagged, not hidden.',
};
/**
 * Three-pillar card strip — sits between the hero and the audience block.
 * Each pillar: short title + Keenan-Sexton gap strip + one punch point.
 *
 * Pillar ordering reflects the customer journey but does NOT imply
 * prerequisites — Marcus can paste a questionnaire immediately (Pillar 2)
 * and the gaps become the Pillar 1 roadmap, not a gate.
 *
 * Critic panel v2 (2026-08-31): Nadia, Marcus, aesthetic all agreed:
 * compact cards, one punch point each, verb-first parallel titles.
 */
export const heroPillars = [
    {
        title: 'Map it.',
        gap: {
            current: 'Framework PDFs, a shared drive, and no single view of what applies.',
            future: 'Your AI systems mapped to the frameworks that apply — gaps scored by risk.',
        },
        punch: 'See exactly what\'s covered and what\'s missing. Gap analysis scored by regulatory risk and business impact.',
    },
    {
        title: 'Prove it.',
        gap: {
            current: 'Questionnaire answers based on what you wish you had.',
            future: 'Responses that cite your uploaded policies — or flag what to upload next.',
        },
        punch: 'Paste a buyer\'s questionnaire. Get draft responses backed by your actual documentation, not framework boilerplate.',
    },
    {
        title: 'Keep it current.',
        gap: {
            current: 'Last questionnaire shipped. No idea if a regulatory change just made your answer wrong.',
            future: 'Critical regulatory changes flagged within 48 hours by a named expert.',
        },
        punch: 'Tracks legislation, framework changes, and case law. Flags when your documentation needs a refresh — before your buyer asks.',
    },
];
/**
 * Three-audience block — parallel entry cards, one per motion.
 *
 * Each card carries a color-coded 4px top-border stripe (--accent-cta-bg
 * amber / --regulatory purple / --success-fg green) to preattentively
 * differentiate the lanes on mobile per glance-critic finding.
 *
 * Copy is job-shaped per the ICP (Nadia's / Anika's / Jordan's language),
 * NOT vendor-jargon. Every card links to the audience's actual next-step
 * surface — no self-referencing anchor loops per the R1 6-lens Blocker
 * finding.
 */
export const threeAudience = {
    heading: 'Which room are you in?',
    subheading: 'Three motions, one platform. Same register, same research, same signed source of truth — with the surface each buyer actually needs.',
    motions: [
        {
            badge: 'Motion 01',
            role: 'SMB Supplier',
            headline: 'Answer the AI section of your prospect\'s questionnaire. Once.',
            body: 'Growth-stage AI SaaS closing enterprise deals faster. Cited draft responses to the 2026 SIG AI section, SOC 2 + AI / ISO 42001 / FedRAMP templates, and a standing AI Disclosure Profile the next buyer can read directly — signed by a named editor.',
            cta: { label: 'See the supplier motion', href: '/supplier' },
            lane: 'supplier',
        },
        {
            badge: 'Motion 02',
            role: 'Enterprise',
            headline: 'Find out which of your suppliers use AI. And which ones never told you.',
            body: 'One disclosure request to every supplier. A scorecard against your own risk bar. Undisclosed AI flagged from your identity logs. Inventory, jurisdiction, and board views when you\'re ready for them.',
            cta: { label: 'See the enterprise motion', href: '/enterprise' },
            lane: 'enterprise',
        },
        {
            badge: 'Motion 03',
            role: 'Assessor · Consultancy',
            headline: 'A signed intelligence feed your firm can consume across engagements.',
            body: 'Signed, versioned research packages. Legislative + framework + case-law + breach intelligence, delivered via cryptographically-verifiable subscription. Standalone SKU — no compliance-tool bundle required.',
            cta: { label: 'See the assessor motion', href: '/research' },
            lane: 'auditor',
        },
    ],
};
/**
 * 5-type AI classification taxonomy — the differentiator no competitor
 * ships. Renders as an editorial spread (breaks the card-grid cascade
 * per aesthetic critic ruling: this is the page's signature moment).
 *
 * Each type carries a text severity token (High / Medium / Lower risk)
 * ALONGSIDE the band color so screen-reader + color-blind users get the
 * distinction per WCAG 1.4.1 (a11y critic finding A6).
 *
 * Content mapped to business-model.md §Register / classification.
 */
export const taxonomy = {
    differentiator: 'No competitor ships this taxonomy.',
    heading: 'Five types of AI. Five cumulative control tiers.',
    subheading: 'Competitors send you an intake questionnaire. gacpro starts from the type of AI you\'re actually running, and applies the controls formally mapped to that tier — plus every framework crosswalk. Agentic system, LLM chatbot, ML model, data pipeline — the register knows the difference.',
    types: [
        {
            numeral: '01',
            name: 'Agentic Systems',
            body: 'Autonomous multi-step AI acting on your behalf. Highest-scrutiny tier: cascade-impact analysis, blast-radius controls, human-in-the-loop attestation.',
            example: 'e.g. Autonomous coding agent · SOC-triage agent · procurement bot',
            risk: 'High risk',
            band: 'red',
        },
        {
            numeral: '02',
            name: 'AI Agents',
            body: 'Bounded task-scoped agents. Controls: capability-scoping, action-audit, reversibility guarantees.',
            example: 'e.g. Support-triage bot · scheduling assistant · data-classification agent',
            risk: 'High risk',
            band: 'red',
        },
        {
            numeral: '03',
            name: 'LLM Services',
            body: 'General-purpose model inference exposed as a service. Controls: input/output monitoring, PII redaction, jailbreak defense, model-provenance provenance.',
            example: 'e.g. Chatbot · copilot · RAG assistant · summarization API',
            risk: 'Medium risk',
            band: 'amber',
        },
        {
            numeral: '04',
            name: 'ML Models',
            body: 'Task-specific trained models. Controls: dataset-lineage, bias-testing, performance-drift monitoring, model-card documentation.',
            example: 'e.g. Fraud detector · pricing model · recommendation engine',
            risk: 'Medium risk',
            band: 'amber',
        },
        {
            numeral: '05',
            name: 'Data Pipelines',
            body: 'The data foundation AI runs on. Controls: source-attestation, PII-flow tracking, retention discipline, cross-border data-transfer controls.',
            example: 'e.g. Training-data ETL · feature store · vector index',
            risk: 'Lower risk',
            band: 'green',
        },
    ],
};
/**
 * Comparative row — buyer-objection-first cards (Keenan-Sexton
 * voice-of-customer). Each card leads with the objection the buyer
 * already carries about their current tool, then names the tool. Vendor
 * names appear once, as bordered mono tokens, not in the card lede.
 *
 * Business-model.md §0.2 is the source; distilled to landing-scale
 * (three cards, three sentences each).
 */
export const comparative = {
    heading: 'Four things every AI compliance register should do — and doesn\'t.',
    subheading: 'If you\'re evaluating gacpro against something you\'re already running, here\'s the sharpened version.',
    cards: [
        {
            objection: 'Vanta or Drata got you certified — but not for the AI-specific frameworks.',
            body: 'We match on the certification pipeline (SOC 2, ISO 42001, FedRAMP path) and differentiate on register curation for emerging AI risk: EU AI Act GPAI, Colorado SB21-169, IL AI Video Interview Act, NYC AEDT, HR / fintech / healthcare tort precedent. AI-first, not SOC-2-first retrofitting AI.',
            vendors: ['Vanta', 'Drata', 'Secureframe'],
        },
        {
            objection: 'Credo AI is a policy library. You still need the operational workflow to close the gaps.',
            body: 'We match on AI-specific risk framing and differentiate on the end-to-end workflow (target → assign → track → deliver) and audit-defensible deliverables. Competitors are policy libraries + assessments; gacpro is the operational system that turns them into evidence.',
            vendors: ['Credo AI', 'Holistic AI', 'Fairly AI', 'IBM watsonx.governance'],
        },
        {
            objection: 'OneTrust or ServiceNow bundles AI governance as a $0-marginal add-on — no AI-specific depth, no state-law tracking.',
            body: 'We match on register + evidence rigor and differentiate on AI-specific curation depth (state AI-law tracking, tort precedent, EU AI Act interpretation) and setup ease — a fraction of the consultant overhead of a bundle expansion.',
            vendors: ['OneTrust', 'ServiceNow GRC', 'AuditBoard', 'MetricStream'],
        },
    ],
};
/**
 * Research subscription pitch section — the auditor / consultancy
 * motion's dedicated block. Includes the Standard / Priority / Real-Time
 * tier ladder Jordan flagged as missing in R1 (the commercial reason his
 * firm would swap out Thomson Reuters).
 *
 * Manifest visual is illustrative, not implementation-detail: one truncated
 * hash anchor + named signer + link to /verify. Aesthetic + Jordan agree
 * on the "prop → proof" bar. No raw SHA-256 in visible chrome.
 */
export const researchSubscription = {
    eyebrow: 'Motion 03 · Research subscription',
    heading: 'A signed intelligence feed for the firms that need to be current.',
    subheading: '3PAO firms + GRC consultancies subscribe to the Research subscription and consume the same intel feed across every engagement. Signed, versioned, cryptographically-verifiable. Standalone SKU — no compliance-tool bundle required.',
    tiers: [
        { name: 'Standard', cadence: 'Weekly batch', latency: 'Up to 7 days' },
        { name: 'Priority', cadence: 'Daily pull + critical alerts', latency: '24–48 hours on critical' },
        { name: 'Real-Time', cadence: 'Continuous streaming', latency: 'Near-real-time' },
    ],
    cta: { label: 'Talk to us about the research feed', href: 'mailto:hello@gacpro.io?subject=Research%20subscription' },
};
/**
 * Manifest visual copy — the auditor-artifact honesty note.
 *
 * Shows ONE representative signed row (per Jordan's "prop → proof"
 * requirement) plus a "…and N more, all signed" summary tail. Deep-links
 * to /verify for the full technical detail. NO raw SHA-256, NO seq
 * numbers, NO curl invocations in visible chrome (per PR-C0-4 clutter
 * rule + aesthetic override).
 */
export const manifestNote = {
    packName: 'gacpro-research-pack',
    packedAt: 'this week',
    representativeRow: {
        frameworkName: 'ISO 42001 · A.6.1.1',
        hashPrefix: 'sha256: 3a7f…c9d1',
        status: 'signed',
    },
    tailNote: 'Plus 6 more framework tiles this week — all signed by the editor of record.',
    signer: 'Signed by Steve Nielsen, GRC Lead',
    verifyLinkLabel: 'View a sample verify response',
    verifyLinkHref: '/verify',
};
/**
 * Post-CTA honesty line for the landing footer / freshness stamp.
 *
 * Anika + Jordan both flagged the single-editor bus-factor as a
 * shelfware / independence concern (R1 Highs). Surface the hiring
 * trigger commitment ABOVE the fold-of-the-day rather than burying it
 * at Buyer's Guide Q7. This uses the existing `editorHonestyLine`
 * verbatim — we just re-render it as a hero-adjacent trust signal, not
 * a deep-page footnote.
 *
 * (The existing `editorHonestyLine` export at line 199 above is
 * canonical; this comment documents the R2 usage decision.)
 */
/**
 * R2 pain pre-headline — dim mono line above the H1.
 *
 * Frames the H1 as the resolution to a pain state the buyer already
 * feels. IBM Plex Mono, 11px, uppercase, 45% opacity on dark hero
 * surfaces (see HeroSection gapContent slot usage in Landing.tsx).
 */
export const heroPainPreHeadline = 'ENTERPRISE DEAL STALLED ON AI COMPLIANCE?';
/**
 * R2 micro-conversion card — email capture between proof strips and
 * diff cards. Low-friction "send me a draft" alternative to the
 * demo-booking CTA. Ships as a centered card with email input.
 */
export const microConversion = {
    heading: 'Want a draft using YOUR frameworks?',
    body: "Drop your email and we'll send one within 24 hours.",
    buttonLabel: 'Send me a draft',
    placeholder: 'you@company.com',
};
/**
 * R2 bottom CTA — updated headline, supporting copy, and urgency line.
 *
 * The urgency line renders in italic below the supporting copy,
 * targeting buyers with an imminent security review (Keenan-Sexton
 * activation trigger).
 */
export const ctaHeadline = 'Paste a questionnaire. Get a cited draft.';
export const ctaSupporting = 'Every claim sourced, every citation linked, every answer signed by a named editor. Try it with the sample report, or book a demo to see your own data.';
export const ctaUrgencyLine = "Security review next week? The sample report shows exactly what your prospect will see.";
/** R2 pricing popular badge text. */
export const pricingPopularBadge = 'Most popular';
//# sourceMappingURL=copy.js.map